<template>
  <form class="colors-form">
    <ul
      ref="listOfColors"
      class="colors"
    >
      <li
        v-for="color in colorsOptions"
        :key="color.id"
        :class="{
          selected: selectedColorId === color.id,
          'out-of-stock': !color.available,
          'margin-top-xxs':isColorWay,
          'color-swatch-sprite-wrapper updated-wrapper': isSpacingEnabled,
          'color-swatch-sprite-wrapper': !isSpacingEnabled
        }"
      >
        <div
          v-if="isColorwayPricing2 && color.onSale"
          class="color-on-sale"
        />
        <label
          :class="{
            'color-swatch-sprite-label updated-label': isSpacingEnabled,
            'color-swatch-sprite-label': !isSpacingEnabled
          }"
        >
          <input
            v-model="selectedColorId"
            type="radio"
            :value="color.id"
            :name="`color-swatches-${name}`"
            class="color-swatch-sprite-radio"
            :aria-label="color.label"
          >
          <slot
            name="swatch-icon"
            :color="color"
          />
          <img
            class="color-swatch-sprite"
            :style="{ transform: `translate(-${color.spriteOffset}px, ${swatchHeight})` }"
            :src="color.img"
            :alt="color.name"
          >
        </label>
      </li>
    </ul>
  </form>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useBrand } from '../../../composables/useBrand';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  colors: {
    /** @type import('vue').PropType<BaseColor[]> */
    type: Array,
    default: () => [],
  },
  spriteUrls: {
    /** @type import('vue').PropType<String[]> */
    type: Array,
    default: () => [],
  },
  modelValue: {
    /** @type import('vue').PropType<ColorId?> */
    type: Number,
    default: null,
  },
  isColorWay: {
    type: Boolean,
    default: false,
  },
  isSpacingEnabled: {
    type: Boolean,
    default: false,
  },
  isSpacing2Enabled: {
    type: Boolean,
    default: false,
  },
  isColorwayPricing2: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['update:modelValue']);

const { isBcom } = useBrand();

const selectedColorId = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emits('update:modelValue', newValue);
  },
});

const listOfColors = ref(null);

const colorsOptions = computed(() => props.colors.map((color) => ({
  ...color,
  label: `Color: ${color.available ? color.name : `${color.name} is unavailable`}`,
  img: color.spriteUrl ? color.spriteUrl : props.spriteUrls[color.spriteIndex ?? -1],
})));

const mcomSwatchHeight = !props.isSpacingEnabled ? '-18px' : '-20px';
const swatchHeight = isBcom ? '0' : mcomSwatchHeight;

defineExpose({ // eslint-disable-line no-undef
  listOfColors,
  colorsOptions,
});
</script>

<style lang="scss" scoped>
@import "~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss";

$outline-color: #4D90FE;

.colors-form {
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  overflow: auto;

  .color-swatch-sprite-radio {
    position: absolute;
    z-index: 0;
  }
}

.colors {
  display: flex;
  @include breakpoint(small only) {
      @include padding-direction('bottom', map-get($spacer, 'xs'));
  }
}

.color-swatch-sprite-label {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  width: 35px;
  height: 35px;
  border: $gray-2-border;
  box-sizing: content-box;
  z-index: 1;
}

.color-swatch-sprite {
  position: relative;
  height: auto;
  max-width: none;
  z-index: 1;
}

.color-swatch-sprite-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 43px;
  min-width: 43px;
  height: 43px;
  margin-right: 8px;
  margin-top: 16px;
}

.color-swatch-sprite-wrapper:focus-within {
 box-shadow: inset 0 0 0 1px $outline-color;
}

.color-swatch-sprite-wrapper:last-of-type {
  @include breakpoint(small only) {
    margin-right: 0;
  }
}

.out-of-stock label:after {
  content: '';
  position: absolute;
  transform: translate(-50%, -50%) rotate(45deg);
  transform-origin: center;
  top: 50%;
  left: 50%;
  width: 150%;
  border-top: 1px solid $gray-2-color;
  border-bottom: 1px solid $white;
  z-index: 2;
}

.selected {
  border: $black-border;
  padding: rem-calc(2);
  box-shadow: inset 0px 0px 0px 1px $white;
}

@include breakpoint(medium up) {
  .colors {
    flex-wrap: wrap;
  }
}

.color-swatch-sprite-wrapper.updated-wrapper {
  width: 39px;
  min-width: 39px;
  height: 39px;
}

.color-swatch-sprite-label.updated-label {
  width: 32px;
  height: 32px;
}

.color-on-sale {
  position: absolute;
  height: rem-calc(4);
  width: rem-calc(4);
  background-color: $sale-color;
  right: 0;
  top: 0;
  border-radius: 50%;
}
</style>

<style lang="scss" brand="mcom" scoped>
.color-swatch-sprite-wrapper {
  border-radius: 50%;
}
.color-swatch-sprite-label {
  border-radius: 50%;
}

.selected {
  .color-on-sale {
    right: -1px;
    top: -1px;
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
.color-on-sale {
  right: -4px;
  top: -4px;
}

.selected {
  .color-on-sale {
    right: -5px;
    top: -5px;
  }
}
</style>
